import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { fetchCrons, type ApiCron } from '../../lib/api'
import { createCron, deleteCron, setCronEnabled, runCronNow, type CronWrite } from '../../lib/cronsApi'
import { useAgentsList } from './useAgents'
import { Panel, Spinner, ErrorState, EmptyState, Badge, Button, Table, type Column } from '../../components/ui'
import { fmtDateTime } from '../../lib/format'
import { useActiveWorkspace } from '../../lib/workspace'

type Frequency = 'daily' | 'weekly' | 'monthly'

// cron day_of_week is 0=Sun..6=Sat; show Mon-first.
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
]
const WD_LABEL = (d: number) => WEEKDAYS.find((w) => w.value === d)?.label ?? String(d)

const TIMEZONES = [
  'UTC', 'Europe/Warsaw', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata', 'Australia/Sydney',
]

function tzLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(new Date())
    let off = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+00:00'
    off = off.replace('GMT', 'UTC')
    if (off === 'UTC') off = 'UTC+00:00'
    return `${tz} (${off})`
  } catch {
    return tz
  }
}

const pad = (n: number) => String(n).padStart(2, '0')
const hhmm = (h: number, m: number) => `${pad(h)}:${pad(m)}`

interface CronGroup {
  key: string
  agentName: string
  ownerLabel: string
  canEdit: boolean
  frequency: string
  timezone: string
  rowIds: string[]
  enabled: boolean
  times: string[]
  daysOfWeek: number[]
  daysOfMonth: number[]
  nextRun: string | null
}

function groupCrons(crons: ApiCron[]): CronGroup[] {
  const map = new Map<string, ApiCron[]>()
  for (const c of crons) {
    const k = `${c.agent_name}|${c.frequency}|${c.timezone}`
    const arr = map.get(k)
    if (arr) arr.push(c)
    else map.set(k, [c])
  }
  const uniqSort = <T extends string | number>(xs: T[]) =>
    [...new Set(xs)].sort((a, b) =>
      typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b)),
    )
  return [...map.entries()].map(([key, rows]) => {
    const enabledNext = rows.filter((r) => r.enabled && r.next_run_at).map((r) => r.next_run_at!)
    return {
      key,
      agentName: rows[0].agent_name,
      ownerLabel: rows[0].created_by || (rows[0].user_id ? rows[0].user_id.slice(0, 8) : 'Global'),
      canEdit: rows.every((r) => r.can_edit),
      frequency: rows[0].frequency,
      timezone: rows[0].timezone,
      rowIds: rows.map((r) => r.id),
      enabled: rows.some((r) => r.enabled),
      times: uniqSort(rows.map((r) => hhmm(r.hour, r.minute))),
      daysOfWeek: uniqSort(rows.map((r) => r.day_of_week).filter((d): d is number => d != null)),
      daysOfMonth: uniqSort(rows.map((r) => r.day_of_month).filter((d): d is number => d != null)),
      nextRun: enabledNext.length ? enabledNext.sort()[0] : null,
    }
  })
}

function whenLabel(g: CronGroup): string {
  const at = g.times.join(', ')
  if (g.frequency === 'daily') return `daily at ${at}`
  if (g.frequency === 'weekly') return `weekly on ${g.daysOfWeek.map(WD_LABEL).join(', ')} at ${at}`
  if (g.frequency === 'monthly') return `monthly on day ${g.daysOfMonth.join(', ')} at ${at}`
  return 'custom schedule'
}

export function CronsTab({ active }: { active: boolean }) {
  const qc = useQueryClient()
  const { activeWorkspaceKey } = useActiveWorkspace()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['crons', activeWorkspaceKey],
    queryFn: () => fetchCrons(activeWorkspaceKey),
    enabled: active && !!activeWorkspaceKey,
  })
  const [editing, setEditing] = useState<CronGroup | 'new' | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)

  if (!activeWorkspaceKey) return <EmptyState title="No active workspace." />
  if (isLoading) return <Spinner />
  if (isError || !data) return <ErrorState message="Couldn't load crons." />

  const groups = groupCrons(data.crons)
  const showOwner = data.crons.some((c) => c.created_by || c.user_id)
  const invalidate = () => qc.invalidateQueries({ queryKey: ['crons', activeWorkspaceKey] })

  const onToggle = async (g: CronGroup) => {
    setBusyKey(g.key)
    try {
      await Promise.all(g.rowIds.map((id) => setCronEnabled(activeWorkspaceKey, id, !g.enabled)))
      invalidate()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Update failed')
    } finally {
      setBusyKey(null)
    }
  }

  const onDelete = async (g: CronGroup) => {
    if (!confirm(`Delete cron — ${g.agentName} ${whenLabel(g)}?`)) return
    setBusyKey(g.key)
    try {
      await Promise.all(g.rowIds.map((id) => deleteCron(activeWorkspaceKey, id)))
      invalidate()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusyKey(null)
    }
  }

  const onRunNow = async (g: CronGroup) => {
    setBusyKey(g.key)
    try {
      await runCronNow(activeWorkspaceKey, g.rowIds[0])
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Run failed')
    } finally {
      setBusyKey(null)
    }
  }

  const columns: Column<CronGroup>[] = [
    { key: 'agent', header: 'Agent', render: (g) => <span className="font-medium">{g.agentName}</span> },
    ...(showOwner ? [{ key: 'owner', header: 'Owner', render: (g: CronGroup) => <span className="text-xs text-gray-500">{g.ownerLabel}</span> } satisfies Column<CronGroup>] : []),
    { key: 'when', header: 'When', render: (g) => <span className="text-gray-700">{whenLabel(g)}</span> },
    { key: 'tz', header: 'Timezone', render: (g) => <span className="text-xs text-gray-500">{tzLabel(g.timezone)}</span> },
    { key: 'status', header: 'Status', render: (g) => (
      <Badge tone={g.enabled ? 'indigo' : 'neutral'}>{g.enabled ? 'enabled' : 'disabled'}</Badge>
    ) },
    { key: 'next', header: 'Next run', render: (g) => <span className="text-xs text-gray-500">{g.enabled ? fmtDateTime(g.nextRun) : '—'}</span> },
    { key: 'actions', header: '', render: (g) => (
      <div className="flex gap-2 justify-end">
        <Button variant="primary" disabled={busyKey === g.key} onClick={() => onRunNow(g)}>Run now</Button>
        {g.canEdit && (
          <>
            <Button variant="secondary" onClick={() => setEditing(g)}>Edit</Button>
            <Button variant="secondary" disabled={busyKey === g.key} onClick={() => onToggle(g)} className={g.enabled ? '!text-red-600' : ''}>
              {g.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Button variant="secondary" disabled={busyKey === g.key} onClick={() => onDelete(g)} className="!text-red-600">Delete</Button>
          </>
        )}
      </div>
    ) },
  ]

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-gray-700">
        A cron emits an event on its schedule; the orchestrator picks up that event and runs the chosen
        agent. The resulting run shows up under Agent runs.
      </div>
      <div className="flex justify-start">
        <Button variant="primary" onClick={() => setEditing('new')}>Create new agent cron</Button>
      </div>
      <Panel title={`Agent crons — ${groups.length}`}>
        {groups.length === 0 ? (
          <EmptyState title="No crons yet." hint="Create one to schedule an agent run." />
        ) : (
          <Table columns={columns} rows={groups} empty="No crons yet." />
        )}
      </Panel>
      {editing && (
        <CronModal
          group={editing === 'new' ? null : editing}
          workspaceKey={activeWorkspaceKey}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate() }}
        />
      )}
    </div>
  )
}

function CronModal({ group, workspaceKey, onClose, onSaved }: { group: CronGroup | null; workspaceKey: string; onClose: () => void; onSaved: () => void }) {
  const { data: agents } = useAgentsList()
  const [agentName, setAgentName] = useState(group?.agentName ?? '')
  const [frequency, setFrequency] = useState<Frequency>((group?.frequency as Frequency) ?? 'daily')
  const [timezone, setTimezone] = useState(group?.timezone ?? 'Europe/Warsaw')
  const [times, setTimes] = useState<string[]>(group?.times.length ? group.times : ['08:00'])
  const [weekdays, setWeekdays] = useState<number[]>(group?.daysOfWeek ?? [1])
  const [monthdaysText, setMonthdaysText] = useState((group?.daysOfMonth ?? [1]).join(', '))
  const [saving, setSaving] = useState(false)

  const setTime = (i: number, v: string) => setTimes((ts) => ts.map((t, j) => (j === i ? v : t)))
  const addTime = () => setTimes((ts) => [...ts, '12:00'])
  const removeTime = (i: number) => setTimes((ts) => (ts.length > 1 ? ts.filter((_, j) => j !== i) : ts))
  const toggleWeekday = (d: number) => setWeekdays((ws) => (ws.includes(d) ? ws.filter((x) => x !== d) : [...ws, d]))

  const buildSpecs = (): CronWrite[] | string => {
    if (!agentName.trim()) return 'Pick an agent.'
    const tlist = times.filter((t) => /^\d{2}:\d{2}$/.test(t))
    if (tlist.length === 0) return 'Add at least one time.'
    const parsed = tlist.map((t) => t.split(':').map(Number) as [number, number])
    const base = { workspace_key: workspaceKey, agent_name: agentName.trim(), timezone, frequency } as const
    if (frequency === 'daily') {
      return parsed.map(([hour, minute]) => ({ ...base, hour, minute }))
    }
    if (frequency === 'weekly') {
      if (weekdays.length === 0) return 'Pick at least one weekday.'
      return weekdays.flatMap((d) => parsed.map(([hour, minute]) => ({ ...base, hour, minute, day_of_week: d })))
    }
    const days = monthdaysText.split(',').map((s) => Number(s.trim())).filter((n) => Number.isInteger(n) && n >= 1 && n <= 31)
    if (days.length === 0) return 'Enter day(s) of month between 1 and 31.'
    return days.flatMap((d) => parsed.map(([hour, minute]) => ({ ...base, hour, minute, day_of_month: d })))
  }

  const onSave = async () => {
    const specs = buildSpecs()
    if (typeof specs === 'string') {
      alert(specs)
      return
    }
    setSaving(true)
    try {
      if (group) await Promise.all(group.rowIds.map((id) => deleteCron(workspaceKey, id)))
      await Promise.all(specs.map((s) => createCron(s)))
      onSaved()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-xl w-[min(560px,90vw)] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{group ? 'Edit agent cron' : 'Create new agent cron'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex flex-col gap-3">
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Agent
            <select value={agentName} onChange={(e) => setAgentName(e.target.value)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md">
              <option value="">Select…</option>
              {(agents ?? []).map((a) => (<option key={a.name} value={a.name}>{a.name}</option>))}
            </select>
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Frequency
            <select value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          {frequency === 'weekly' && (
            <div className="text-xs text-gray-700 flex flex-col gap-1">
              Days of week
              <div className="flex flex-wrap gap-1">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleWeekday(d.value)}
                    className={`px-2 py-1 rounded-md border text-xs ${weekdays.includes(d.value) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {frequency === 'monthly' && (
            <label className="text-xs text-gray-700 flex flex-col gap-1">
              Day(s) of month (1–31, comma-separated)
              <input value={monthdaysText} onChange={(e) => setMonthdaysText(e.target.value)} placeholder="1, 15" className="px-2 py-1.5 text-xs border border-gray-300 rounded-md" />
            </label>
          )}
          <div className="text-xs text-gray-700 flex flex-col gap-1">
            Time(s)
            <div className="flex flex-col gap-1.5">
              {times.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="time" value={t} onChange={(e) => setTime(i, e.target.value)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md" />
                  {times.length > 1 && (
                    <button type="button" onClick={() => removeTime(i)} className="text-gray-400 hover:text-red-600"><X size={14} /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addTime} className="self-start text-xs text-indigo-600 hover:underline">+ add time</button>
            </div>
          </div>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Timezone
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md">
              {TIMEZONES.map((tz) => (<option key={tz} value={tz}>{tzLabel(tz)}</option>))}
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  )
}
